<script setup lang="ts">
import DropDown, { type DropDownItem } from '@/components/common/DropDown.vue';
import FormattedNumberInput from '@/components/common/formatted-number-input.vue';
import { ataService } from '@/services/ata/ata-service';
import { supplierInvoiceService } from '@/services/invoices/supplier-invoice-service';
import { notificationService } from '@/services/notification/notification-service';
import { fetchAccounts } from '@/utils/accounts/fetch-accounts';
import {
  PhArrowElbowDownRight,
  PhArrowsSplit,
  PhFloppyDisk,
  PhTrash,
} from '@phosphor-icons/vue';
import { models, type Nullish, utils } from 'cesg-shared';
import { onMounted, type PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, useRoute } from 'vue-router';

interface HideProps {
  notes?: boolean;
}

interface Disable {
  notes?: boolean;
  splitInvoice?: boolean;
}

const props = defineProps({
  notes: {
    type: String,
    required: true,
    default: () => '',
  },
  rows: {
    type: Array as PropType<models.SupplierInvoiceRow[]>,
    required: true,
  },
  vat: {
    type: [Number, null] as PropType<number | null>,
    required: false,
    default: null,
  },
  invoiceId: {
    type: String,
    required: true,
  },
  projectId: {
    type: String,
    required: true,
  },
  hasPermissionInvoice: {
    type: Function as PropType<
      (permissionType: models.PermissionType) => boolean
    >,
    required: true,
    default: () => () => false,
  },
  hide: {
    type: Object as PropType<HideProps>,
    required: false,
    default: () => ({
      notes: false,
    }),
  },
  disable: {
    type: Object as PropType<Disable>,
    required: false,
    default: () => ({
      notes: false,
      splitInvoice: false,
    }),
  },
});

const { t } = useI18n();
const emit = defineEmits(['saved']);
const route = useRoute();

const invoiceRows = ref<models.SupplierInvoiceRow[]>([]);
const originalRows = ref<models.SupplierInvoiceRow[]>([]);
const notesInvoice = ref<Nullish<string>>('');
const originalNotesInvoice = ref<Nullish<string>>('');
const invoiceId = props.invoiceId as string;
const loading = ref<boolean>(false);
interface dropdownItem {
  value: string;
  text: string;
}
const atas = ref<dropdownItem[]>([]);
const accounts = ref<DropDownItem[]>();
const errors = ref<Record<string, boolean>>({});

async function saveInvoice(): Promise<boolean> {
  return await saveData();
}

const addChild = (rowId: string) => {
  const row = invoiceRows.value.find((item) => item.id === rowId);
  if (!row) return;

  const newSplit: models.SupplierInvoiceSplitRow = {
    accountNumber: 0,
    accountName: '',
    projectId: row.projectId,
    projectName: row.projectName,
    amount: 0,
    ataNumber: null,
    ataName: '',
  };

  row.splitRows = row.splitRows || [];
  row.splitRows.push(newSplit);
};

const getAtas = async () => {
  const projectId = utils.type.asType<string>(route.params.id);
  if (!projectId) return;

  const res = await ataService.getOnlyForcastedAtas(projectId);

  res.forEach((ata) => {
    atas.value.push({
      value: ata.serialNumber?.toString() || '',
      text: `${ata.serialNumber} - ${ata.title}`,
    });
  });
};

const getAccounts = async () => {
  accounts.value = await fetchAccounts();
};

const getAccountName = (accountNumber: number) => {
  const account = accounts.value?.find(
    (account) => account.value === accountNumber.toString(),
  );
  if (account) {
    const textParts = account.text.split(' - ');
    return textParts.length > 0 ? textParts[1].trim() : '';
  } else {
    return '';
  }
};

const removeChild = (rowId: string, index: number) => {
  const row = invoiceRows.value.find((item) => item.id === rowId);
  if (!row || !row.splitRows || index < 0 || index >= row.splitRows.length)
    return;

  parentCalculatedAmount(row);

  row.splitRows.splice(index, 1);
};

const parentCalculatedAmount = (row: models.SupplierInvoiceRow) => {
  return (
    // 3@tag:vera-2.5:not-here - the debit and credit should work as expected with excl vat
    row.debit -
    row.credit -
    row.splitRows.reduce((acc, child) => acc + child.amount, 0)
  );
};

const validateFields = (): boolean => {
  let isValid = true;

  invoiceRows.value.forEach((row) => {
    row.splitRows?.forEach((split, index) => {
      if (!split.accountNumber || split.accountNumber == 0) {
        errors.value[`${row.id}-${index}`] = true;
        isValid = false;
      } else {
        errors.value[`${row.id}-${index}`] = false;
      }
    });
  });

  return isValid;
};

const saveData = async (): Promise<boolean> => {
  if (!validateFields()) {
    return false;
  }

  loading.value = true;

  const transformedData: models.UpdateSupplierInvoiceBody = {
    notes: notesInvoice.value,
    project: props.projectId,
    rows: invoiceRows.value.map((row) => {
      const accountName = getAccountName(row.accountNumber);
      return {
        id: row.id,
        accountNumber: row.accountNumber,
        accountName,
        projectId: row.projectId,
        projectName: row.projectName,
        debit: row.debit,
        credit: row.credit,
        ataNumber: row.ataNumber,
        ataName: row.ataName,
        splitRows: row.splitRows.map((child) => ({
          accountNumber: child.accountNumber,
          accountName: getAccountName(child.accountNumber),
          projectId: child.projectId,
          projectName: child.projectName,
          amount: child.amount,
          ataNumber: child.ataNumber,
          ataName: child.ataName,
        })),
      };
    }),
  };

  try {
    const response = await supplierInvoiceService.updateSupplierInvoice(
      invoiceId,
      transformedData,
    );

    if (response?.rows?.length > 0) {
      Object.assign(invoiceRows.value, response.rows);
      originalRows.value = utils.json.deepCopy(invoiceRows.value);
      notesInvoice.value = response.notes;
      originalNotesInvoice.value = response.notes;
    }
    notificationService.open({
      text: t('notifications.saveSuccess'),
      type: 'success',
    });
    emit('saved', response);
    return true;
  } catch (error) {
    console.error(error);
    notificationService.open({
      text: t('notifications.saveError'),
      type: 'danger',
    });
    return false;
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  Object.assign(invoiceRows.value, props.rows);
  originalRows.value = utils.json.deepCopy(invoiceRows.value);
  notesInvoice.value = props.notes;
  originalNotesInvoice.value = props.notes;
  await getAccounts();
  await getAtas();
});

onBeforeRouteLeave((to, from, next) => {
  const rowsChanged =
    JSON.stringify(invoiceRows.value) !== JSON.stringify(originalRows.value);
  const notesChanged = notesInvoice.value !== originalNotesInvoice.value;
  if (rowsChanged || notesChanged) {
    const confirmLeave = window.confirm(t('unsavedChangesNotification'));

    if (confirmLeave) {
      next();
    } else {
      next(false);
    }
  } else {
    next();
  }
});

defineExpose({
  saveInvoice,
});

const shouldHideSaveButton =
  (props.hide.notes || props.disable.notes) && props.disable.splitInvoice;
</script>

<template>
  <div>
    <table
      class="table is-fullwidth"
      v-if="hasPermissionInvoice(models.PermissionType.VIEW)"
    >
      <thead>
        <tr class="header-row">
          <th class="fixed-width-konto">
            {{ t('invoices.splitInvoice.account') }}
          </th>
          <th class="fixed-width-project">
            {{ t('invoices.splitInvoice.project') }}
          </th>
          <th class="fixed-width-summa">
            {{ t('invoices.splitInvoice.amount') }}
          </th>
          <th class="fixed-width-ata">{{ t('invoices.splitInvoice.ata') }}</th>
          <th class="fixed-width-actions"></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in invoiceRows" :key="row.id">
          <tr class="parent-row">
            <td class="align-center fixed-width-konto">
              <drop-down
                :items="accounts ?? []"
                v-model="row.accountNumber"
                :label="t('dropdownLabel')"
                :disabled="disable.splitInvoice"
              />
            </td>
            <td class="align-center fixed-width-project">
              {{ row.projectName }}
            </td>
            <td class="align-center fixed-width-summa">
              {{ utils.formatNumber(parentCalculatedAmount(row)) }}
            </td>
            <td class="align-center fixed-width-ata">
              <DropDown
                :items="atas"
                v-model="row.ataNumber"
                :disabled="disable.splitInvoice"
              />
            </td>
            <td class="align-center is-narrow fixed-width-actions">
              <button
                class="button is-small split"
                @click="addChild(row.id)"
                :disabled="disable.splitInvoice"
                v-if="!disable.splitInvoice"
              >
                <PhArrowsSplit :size="20" />
              </button>
            </td>
          </tr>
          <tr
            v-for="(split, index) in row.splitRows"
            :key="index"
            class="child-row"
          >
            <td class="align-center fixed-width-konto">
              <div class="flex-align-center">
                <PhArrowElbowDownRight :size="16" weight="bold" class="mr-2" />
                <drop-down
                  :items="accounts ?? []"
                  v-model="split.accountNumber"
                  :label="t('dropdownLabel')"
                  :disabled="disable.splitInvoice"
                />
              </div>
              <p
                v-if="errors[`${row.id}-${index}`]"
                :id="`error-${row.id}-${index}`"
                class="help is-danger"
              >
                {{ t('errorSelectAccount') }}
              </p>
            </td>
            <td class="align-center fixed-width-project">
              {{ split.projectName }}
            </td>
            <td class="align-center fixed-width-summa">
              <formatted-number-input
                data-test="split-row-amount-input"
                v-model.number="split.amount"
                :disabled="disable.splitInvoice"
              />
            </td>
            <td class="align-center fixed-width-ata">
              <DropDown
                :items="atas"
                v-model="split.ataNumber"
                :disabled="disable.splitInvoice"
              />
            </td>
            <td class="align-center is-narrow fixed-width-actions">
              <button
                class="button is-small is-danger"
                @click="removeChild(row.id, index)"
                :disabled="disable.splitInvoice"
                v-if="!disable.splitInvoice"
              >
                <PhTrash :size="18" />
              </button>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <div class="field" v-if="!hide.notes">
      <label class="label">{{ t('invoices.splitInvoice.notes') }}</label>
      <div class="control">
        <textarea
          class="textarea"
          id="notes"
          v-model="notesInvoice"
          :disabled="disable.notes"
        ></textarea>
      </div>
    </div>

    <div class="has-text-right mt-4" v-if="!shouldHideSaveButton">
      <button
        class="button"
        :class="{ 'is-loading': loading }"
        @click="saveData"
        :disabled="disable.notes"
      >
        <PhFloppyDisk :size="18" class="mr-2" />
        {{ t('invoices.splitInvoice.save') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.table {
  .fixed-width-konto,
  .fixed-width-project,
  .fixed-width-summa {
    width: 25%;
  }

  .fixed-width-ata {
    width: 20%;
  }

  .fixed-width-actions {
    width: 5%;
  }

  tbody tr {
    &.parent-row {
      background-color: var(--parent-invoice);
      font-weight: 500;
      .split svg {
        transform: rotate(270deg);
      }
    }

    &.child-row {
      background-color: var(--splitted-invoice);
    }

    .input {
      max-width: 100%;
    }

    .flex-align-center {
      display: flex;
      align-items: center;
    }

    .align-center {
      vertical-align: middle;
    }
  }
}

.textarea:disabled {
  color: inherit;
  opacity: 1;
  -webkit-text-fill-color: currentColor;
  cursor: default;
}
</style>
